"use client";

import { wrapper } from "@/redux/Store";
import withReduxSaga from "next-redux-saga";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import Design from "@/redux/models/Design";
import Checkout from "@/redux/models/Checkout";
import Order from "@/redux/models/Order";
import { toast } from "react-toastify";
import Customer from "@/redux/models/Customer";
import useOrder from "@/hooks/useOrder";
import * as Sentry from "@sentry/react";
import { ErrorFallback } from "@/components/ErrorFallback";
import { initSentry } from "@/lib/lazySentry";
import { ORDER_ON_PRODUCTION_ALLOWED_ROUTES, ORDER_ON_PROGRESS_STATUS } from "@/constants/order";
import { useParams, usePathname, useRouter } from "next/navigation";

const Wrapper = ({ children }) => {
  const { orderId, designId } = useParams();
  const pathname = usePathname();
  const router = useRouter();
  const { order } = useOrder();
  const customer = useSelector(Customer.selectors.getCustomer);
  const dispatch = useDispatch();

  useEffect(() => {
    if (orderId) {
      const cb = (newOrder) => {
        const isInProgress = ORDER_ON_PROGRESS_STATUS.includes(newOrder.status);
        const isAllowedIfOrderOnProduction = ORDER_ON_PRODUCTION_ALLOWED_ROUTES.some((route) =>
          pathname.match(route)
        );

        if (!isInProgress && !isAllowedIfOrderOnProduction) {
          dispatch(Order.types.clear());
          dispatch(Design.types.delete());
          toast.error("Order already on production or completed");
          router.push("/shop");
          return;
        }

        if (!newOrder.shipments.length)
          dispatch(Checkout.types.updateCheckout({ shippingQuotes: null }));
      };
      if (!pathname.includes("checkout")) {
        dispatch(
          Order.types.retrieveOrder({
            orderId,
            cb,
            onRedirect: (redirectUrl) => router.push(redirectUrl),
          })
        );
      }
    }

    if (designId) {
      dispatch(
        Design.types.retrieveDesign({
          designId,
          cb: () => {
            dispatch(
              Checkout.types.updateCheckout({
                selectedDesign: designId,
              })
            );
          },
        })
      );
    }
  }, [orderId, designId]);

  useEffect(() => {
    if (orderId !== order?.id) return;

    if (order?.id && order?.customer && !customer?.customer_id) {
      dispatch(Customer.types.retrieveCustomerDetail());
    }
  }, [order?.id, orderId, customer]);

  useEffect(() => {
    initSentry();
  }, []);

  return children;
};

const WrapperWithState = wrapper.withRedux(withReduxSaga(Wrapper));

const withState = (WrappedComponent) => {
  return (props) => (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <WrapperWithState>
        <WrappedComponent {...props} />
      </WrapperWithState>
    </Sentry.ErrorBoundary>
  );
};

export default withState;
